import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Palette, Mail, Phone, RefreshCw, ExternalLink, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LeadSourceChip } from './ConsultationSource';
import { takeAdminParam } from '../../lib/adminLinks';
import { isWaitingFabric } from '../../lib/waitingRequests';
import { HelpLink } from './HelpLink';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_STYLE = {
  new: 'bg-amber-100 text-amber-800',
  contacted: 'bg-blue-100 text-blue-800',
  quoted: 'bg-purple-100 text-purple-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-neutral-200 text-neutral-600',
};

const RequestCard = ({ item, onStatus, focused }) => (
  <div className={`rounded-xl border bg-white p-4 space-y-3 ${focused ? 'border-amber-400 ring-2 ring-amber-200' : ''}`} data-testid={`fabric-quote-${item.id}`} id={`fabric-quote-${item.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-medium">{item.name}</p>
        <div className="flex flex-wrap gap-3 text-xs text-neutral-500 mt-0.5">
          <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:text-amber-700"><Mail className="w-3 h-3" />{item.email}</a>
          {item.phone && <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 hover:text-amber-700"><Phone className="w-3 h-3" />{item.phone}</a>}
          <span>{new Date(item.created_at).toLocaleString()}</span>
          {item.window_count && <span>{item.window_count} windows</span>}
        </div>
        <LeadSourceChip c={item} testPrefix="fabric-quote-source" />
      </div>
      <Select value={item.status} onValueChange={(v) => onStatus(item.id, v)}>
        <SelectTrigger className="w-36 h-8" data-testid={`fabric-quote-status-${item.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {item.fabrics.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1.5 text-xs bg-neutral-100 rounded-full pl-1 pr-2.5 py-1" title={`${f.brand_name || ''} ${f.collection_name || ''}`}>
          <span className="w-4 h-4 rounded-full border" style={{ backgroundColor: f.hex_color || '#ccc' }} />
          {f.name}{f.code ? <code className="text-[10px] text-neutral-500">{f.code}</code> : null}
        </span>
      ))}
    </div>
    {item.notes && <p className="text-sm text-neutral-700 whitespace-pre-line border-l-2 border-amber-300 pl-3">{item.notes}</p>}
    {item.shortlist_url && (
      <a href={item.shortlist_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline">
        <ExternalLink className="w-3 h-3" /> Open shortlist
      </a>
    )}
  </div>
);

export const FabricQuoteRequestsTab = ({ token }) => {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [waitingOnly, setWaitingOnly] = useState(() => takeAdminParam('filter') === 'unlinked');
  const [focusId] = useState(() => takeAdminParam('request'));
  useEffect(() => {
    if (focusId && data.items.some((i) => i.id === focusId)) document.getElementById(`fabric-quote-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [focusId, data.items]);
  const shown = waitingOnly ? data.items.filter(isWaitingFabric) : data.items;

  useEffect(() => {
    axios.get(`${API_URL}/cron/quote-digest/last`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setDigest(data)).catch(() => {});
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get(`${API_URL}/admin/fabric-quote-requests`, { params, headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {
      toast.error('Failed to load fabric quote requests');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/admin/fabric-quote-requests/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="fabric-quote-requests-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800 flex items-center gap-2"><Palette className="w-6 h-6 text-amber-600" /> Fabric Quote Requests</h2>
          <p className="text-neutral-500 mt-1">Shortlists customers sent from the fabric gallery · {data.total} total</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="fabric-quotes-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>

      <div className="flex items-start gap-3 rounded-xl border bg-neutral-50 px-4 py-3 text-sm" data-testid="quote-digest-status">
        <BellRing className="w-4 h-4 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium">Daily follow-up digest · 9:00 AM New York</p>
          <p className="text-neutral-500 text-xs">
            Your team gets an email every morning listing requests still marked <strong>new</strong>. Mark a request as contacted to drop it from the digest.
            {digest?.finished_at && ` Last run ${new Date(digest.finished_at).toLocaleString()} — ${digest.new_count} new, ${digest.sent ? 'email sent' : 'no email needed'}.`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="fabric-quotes-filters">
        <button type="button" onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs border ${filter === 'all' ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200'}`}>All ({data.total})</button>
        {STATUSES.map((s) => (
          <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`fabric-quotes-filter-${s}`} className={`px-3 py-1 rounded-full text-xs border capitalize ${filter === s ? 'bg-amber-600 text-white border-amber-600' : 'border-neutral-200'}`}>
            {s} ({data.counts?.[s] ?? 0})
          </button>
        ))}
        <button type="button" onClick={() => { if (!waitingOnly) setFilter('all'); setWaitingOnly(!waitingOnly); }} data-testid="fabric-quotes-filter-unlinked" className={`px-3 py-1 rounded-full text-xs border ${waitingOnly ? 'bg-amber-600 text-white border-amber-600' : 'border-neutral-200'}`}>
          No Sales Quote yet ({data.items.filter(isWaitingFabric).length})
        </button>
        <HelpLink topic="waiting-for-quote" />
      </div>

      {loading && data.items.length === 0 ? (
        <p className="text-neutral-500 py-10 text-center">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 text-neutral-500" data-testid="fabric-quotes-empty">{waitingOnly ? 'No open fabric quote requests are waiting for a Sales Quote.' : 'No fabric quote requests yet.'}</div>
      ) : (
        <div className="space-y-3">
          {shown.map((item) => (
            <div key={item.id} className="relative">
              <Badge className={`absolute -top-2 left-4 capitalize ${STATUS_STYLE[item.status]}`}>{item.status}</Badge>
              <RequestCard item={item} onStatus={updateStatus} focused={item.id === focusId} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FabricQuoteRequestsTab;
